import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  X, 
  AtSign, 
  Users, 
  Search
} from 'lucide-react';
import axios from 'axios';


const API = "/api";

const UserTaggingInput = ({
  content,
  onContentChange,
  taggedUsers = [],
  onTaggedUsersChange,
  placeholder = "Type your note...",
  productId = null,
  inputRef = null
}) => {
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);
  const selectorRef = useRef(null);

  // Use the passed ref if provided, otherwise use the internal ref
  const combinedRef = inputRef || textareaRef;

  // Get current user
  const getCurrentUser = () => {
    const user = JSON.parse(localStorage.getItem('exled_user'));
    return user;
  };

  // Fetch users for tagging
  const fetchUsers = async (query = '') => {
    setLoading(true);
    try {
      let users = [];

      if (productId && !query) {
        // If productId is provided and no search query, get accessible users for the product
        const response = await axios.get(`${API}/products/${productId}/accessible-users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        });
        users = response.data;
      } else {
        // Otherwise, search all users (but show all users initially, then filter if query provided)
        const response = await axios.get(`${API}/users/search`, {
          params: query ? { q: query } : { q: '' }
        });
        users = response.data;
      }
      
      const currentUser = getCurrentUser();
      // Filter out the current user from the available users list
      const filteredUsers = currentUser
        ? users.filter(user => user.id !== currentUser.id)
        : users;
      console.log('UserTaggingInput: Fetched users:', users.length, 'Filtered users:', filteredUsers.length);
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAvailableUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showUserSelector) {
      fetchUsers(searchQuery);
    }
  }, [showUserSelector]);

  // Separate effect for search query changes
  useEffect(() => {
    if (showUserSelector && searchQuery) {
      fetchUsers(searchQuery);
    }
  }, [searchQuery]);

  // Parse @ mentions from content
  const parseAtMentions = (text) => {
    const atMentionRegex = /@([a-zA-Z0-9._-]+)/g;
    const mentions = [];
    let match;
    while ((match = atMentionRegex.exec(text)) !== null) {
      mentions.push(match[1]);
    }
    return mentions;
  };

  // Check if @everyone is mentioned
  const hasEveryoneMention = (text) => {
    return text.toLowerCase().includes('@everyone');
  };

  // Handle text area content changes
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    onContentChange(newContent);
  };

  // Handle user checkbox change
  const handleUserCheckboxChange = (user, isChecked) => {
    if (isChecked) {
      // Add user if not already tagged
      if (!taggedUsers.find(u => u.id === user.id)) {
        onTaggedUsersChange([...taggedUsers, user]);
      }
    } else {
      // Remove user if tagged
      onTaggedUsersChange(taggedUsers.filter(u => u.id !== user.id));
    }
  };

  // Handle @everyone checkbox change
  const handleEveryoneCheckboxChange = async (isChecked) => {
    if (isChecked && productId) {
      try {
        // Fetch all accessible users
        const response = await axios.get(`${API}/products/${productId}/accessible-users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        });
        const accessibleUsers = response.data;
        const currentUser = getCurrentUser();
        
        // Filter out current user
        const filteredUsers = currentUser 
          ? accessibleUsers.filter(user => user.id !== currentUser.id)
          : accessibleUsers;
        
        // Replace tagged users with all accessible users
        onTaggedUsersChange(filteredUsers);
      } catch (error) {
        console.error('Failed to fetch accessible users for @everyone:', error);
      }
    } else if (!isChecked) {
      // Clear all tagged users when @everyone is unchecked
      onTaggedUsersChange([]);
    }
  };

  // Remove user from tagged list
  const removeTaggedUser = (userId) => {
    onTaggedUsersChange(taggedUsers.filter(u => u.id !== userId));
  };

  
  // Handle @ key press
  const handleKeyDown = (e) => {
    if (e.key === '@') {
      // Small delay to let the @ character be added to the textarea
      setTimeout(() => {
        setShowUserSelector(true);
      }, 100);
    } else if (e.key === 'Escape' && showUserSelector) {
      setShowUserSelector(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setShowUserSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="space-y-3">
      {/* Tagged Users Display */}
      {(taggedUsers.length > 0 || hasEveryoneMention(content)) && (
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-1 text-sm text-slate-600">
            <Users className="w-4 h-4" />
            <span>Tagged:</span>
          </div>
          
          {/* Show @everyone tag if mentioned */}
          {hasEveryoneMention(content) && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200"
            >
              <span className="w-3 h-3 flex items-center justify-center">🌐</span>
              @everyone
              <button
                onClick={() => {
                  // Remove @everyone by removing it from content
                  const newContent = content.replace(/@everyone/gi, '');
                  onContentChange(newContent);
                  onTaggedUsersChange([]);
                }}
                className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {/* Show individual user tags */}
          {!hasEveryoneMention(content) && taggedUsers.map((user) => (
            <Badge
              key={user.id}
              variant="secondary"
              className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200"
            >
              <AtSign className="w-3 h-3" />
              {user.username}
              <button
                onClick={() => removeTaggedUser(user.id)}
                className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Text Area */}
      <div className="relative">
        <textarea
          ref={combinedRef}
          value={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full min-h-[100px] p-3 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />

        {/* User Selector Dropdown */}
        {showUserSelector && (
          <div
            ref={selectorRef}
            className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-96 overflow-hidden"
          >
            {/* Search Header */}
            <div className="p-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 outline-none text-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* Users List with Checkboxes */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  Loading users...
                </div>
              ) : availableUsers.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  No users found
                </div>
              ) : (
                <>
                  {/* @everyone option (only show when productId is provided) */}
                  {productId && (
                    <div className="p-3 border-b border-slate-100 bg-purple-50">
                      <label className="flex items-center gap-3 cursor-pointer hover:bg-purple-100 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={taggedUsers.length > 2} // Assume @everyone if many users are tagged
                          onChange={(e) => handleEveryoneCheckboxChange(e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            🌐
                          </div>
                          <div>
                            <div className="font-medium text-purple-900">
                              @everyone
                            </div>
                            <div className="text-xs text-purple-600">
                              All users with product access
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  )}
                  
                  {/* Individual users */}
                  {availableUsers.map((user) => {
                    const isTagged = taggedUsers.some(u => u.id === user.id);
                    return (
                      <label
                        key={user.id}
                        className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={isTagged}
                          onChange={(e) => handleUserCheckboxChange(user, e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                          {user.full_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {user.full_name}
                          </div>
                          <div className="text-sm text-slate-500 truncate">
                            @{user.username}
                          </div>
                        </div>
                        {isTagged && (
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                      </label>
                    );
                  })}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between text-sm">
                <div className="text-slate-600">
                  {taggedUsers.length} user{taggedUsers.length !== 1 ? 's' : ''} selected
                </div>
                <button
                  onClick={() => setShowUserSelector(false)}
                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Manual User Selector Button */}
      <div className="flex justify-between items-center">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowUserSelector(!showUserSelector)}
          className="flex items-center gap-2"
        >
          <AtSign className="w-4 h-4" />
          Tag Users
        </Button>
        
        {taggedUsers.length > 0 && (
          <div className="text-xs text-slate-500">
            {taggedUsers.length} user{taggedUsers.length !== 1 ? 's' : ''} selected
          </div>
        )}
      </div>
    </div>
  );
};

export default UserTaggingInput;